/**
 * Veredito do beta — a resposta do porteiro, compartilhada por quem precisa
 * dela.
 *
 * Isto vivia dentro do `BetaAccessGate`. Saiu de lá no BUG-93, quando uma
 * segunda superfície passou a precisar da mesma resposta: a `/` decidia entre
 * landing e app olhando só a **existência de sessão**, e mandava quem está na
 * FILA do beta pra `/home` — que é `Guarded`, cai no gate e volta pra tela de
 * beta fechado. Toda porta levava ao mesmo lugar, e a única saída era deslogar.
 *
 * Sessão ≠ acesso desde que o beta virou fechado. Quem responde isso é aqui, e
 * é UM lugar só: duas cópias divergiriam, e a divergência não teria sintoma
 * visível — só uma tela mandando pra outra.
 *
 * O veredito é server-side (`checkBetaAccess` lê o e-mail do token verificado
 * contra a `beta_allowlist`, que é server-only nas rules). O client só roteia.
 *
 * DUAS DECISÕES DE DISPONIBILIDADE, ambas deliberadas e herdadas do gate:
 *
 * 1. Cache em escopo de módulo — a callable é chamada UMA vez por carga de
 *    app, não a cada navegação nem por superfície. Sem isso, cada troca de
 *    rota pagaria um round-trip, e a `/` passaria a cobrar um segundo.
 * 2. Fail-open em erro — se a callable falhar (rede, cold start, outage), o
 *    app abre. Uma indisponibilidade do porteiro não pode trancar todo mundo
 *    do lado de fora; é a mesma escolha do gate ausente = OFF no servidor.
 *    Consequência aceita: durante uma falha da callable, um e-mail fora da
 *    lista entra. Pra um beta privado onde todos já são autenticados, isso é
 *    melhor que derrubar o acesso de quem é legítimo.
 */

import { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { checkBetaAccess, type CheckBetaAccessResult } from "../services/firebase/betaService";
import { traceStep } from "../lib/boot/bootTrace";
import { withTimeout } from "../lib/boot/withTimeout";

/**
 * Uma chamada por usuário por carga de app, compartilhada entre as superfícies.
 * Chaveado por uid, não um singleton solto: o "entrar com outra conta" da
 * BetaClosed troca de sessão SEM recarregar a página — com cache global, o
 * veredito do usuário anterior valeria para o próximo.
 */
let cachedUid: string | null = null;
let verdictPromise: Promise<CheckBetaAccessResult> | null = null;

/**
 * ── Semente DURÁVEL do veredito (BUG-198, 01/09) ────────────────────────────
 *
 * Medido no Cloud Logging: o `checkBetaAccess` em COLD START do Cloud Run leva
 * ~12-17s (request 16:35:57 → "verification passed" 16:36:09; três "Starting
 * new instance" na mesma janela) — acima do teto de 10s do boot. Com o beta
 * pequeno, a instância escala a zero entre usos, então TODO boot fora de pico
 * pagava o teto inteiro de tela parada antes do fail-open (`BOOT_BETA_TIMEOUT`
 * no diário do fundador).
 *
 * A semente é o último veredito CONFIRMADO pelo servidor, por uid: o boot abre
 * com ele na hora e a callable revalida por trás — se o veredito mudou, o
 * estado vivo vence a semente e a tela flipa (aprovado na fila entra sem
 * reinstalar; removido da lista cai na BetaClosed na mesma sessão).
 *
 * ⚠️ Isto NÃO afrouxa segurança: o gate de beta é roteamento de UI — quem
 * protege dado são as Rules, que nunca leram a allowlist. E o fail-open de
 * timeout/erro JÁ deixava qualquer sessão entrar durante uma falha do
 * porteiro; a semente só troca "10s de spinner + fail-open" por "última
 * resposta real do servidor". Fail-open NÃO grava semente (não é veredito).
 */
const VERDICT_STORE_PREFIX = "livra:beta_verdict:";

function readStoredVerdict(uid: string): Exclude<BetaVerdict, "pending"> | null {
  try {
    const v = window.localStorage.getItem(VERDICT_STORE_PREFIX + uid);
    return v === "allowed" || v === "blocked" ? v : null;
  } catch {
    return null; // modo privado / storage bloqueado: sem semente, fluxo de hoje
  }
}

function writeStoredVerdict(uid: string, value: Exclude<BetaVerdict, "pending">) {
  try {
    window.localStorage.setItem(VERDICT_STORE_PREFIX + uid, value);
  } catch {
    // sem storage a semente simplesmente não existe na próxima carga
  }
}

/**
 * ⚠️ O teto de 10s não é zelo: sem ele esta é mais uma porta de spinner eterno.
 * O `.catch` abaixo cobre uma promessa que REJEITA; uma que simplesmente nunca
 * assenta (callable pendurada, cold start que não volta, rede que engole o
 * pacote) deixa `resolved` em `null` para sempre, e tanto a `/` quanto toda
 * rota `Guarded` ficam em `"pending"` — que é exatamente o estado de spinner.
 *
 * O estouro cai no MESMO fail-open já decidido para o erro (ver cabeçalho): o
 * app abre. Uma indisponibilidade do porteiro não tranca quem é legítimo.
 */
function getVerdict(uid: string): Promise<CheckBetaAccessResult> {
  if (cachedUid !== uid || !verdictPromise) {
    cachedUid = uid;
    verdictPromise = traceStep("beta-verdict", () =>
      withTimeout(checkBetaAccess(), "BOOT_BETA_TIMEOUT"),
    );
  }
  return verdictPromise;
}

export type BetaVerdict = "pending" | "allowed" | "blocked";

/**
 * `"allowed"` quando não há sessão: sem e-mail não existe quem barrar, e é o
 * que faz o hook ser inerte na `/` do visitante anônimo (nenhuma chamada sai).
 * Quem exige sessão é o `ProtectedRoute`, uma camada acima do gate.
 */
export function useBetaVerdict(): BetaVerdict {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  // O veredito carrega o uid a que pertence e o estado é DERIVADO na render.
  // Assim a troca de conta volta pra "pending" sozinha, sem um setState
  // síncrono dentro do efeito (que dispara render em cascata).
  const [resolved, setResolved] = useState<{
    uid: string;
    value: Exclude<BetaVerdict, "pending">;
  } | null>(null);

  useEffect(() => {
    if (!uid) return;
    let alive = true;
    getVerdict(uid)
      .then((v) => {
        const value = v.gateEnabled && !v.allowed ? "blocked" : ("allowed" as const);
        // A semente só nasce de resposta REAL do servidor — o fail-open abaixo
        // não grava, senão uma indisponibilidade do porteiro viraria "allowed"
        // durável para quem está na fila.
        writeStoredVerdict(uid, value);
        if (alive) setResolved({ uid, value });
      })
      .catch(() => {
        // Fail-open com nuance de SEMENTE (BUG-198): sem veredito conhecido, a
        // indisponibilidade do porteiro não tranca ninguém (doutrina do
        // cabeçalho). COM veredito conhecido, a última resposta REAL vence a
        // falha — quem foi confirmado na fila não entra só porque o porteiro
        // caiu, e quem foi confirmado dentro não é trancado por um timeout.
        if (alive) setResolved({ uid, value: readStoredVerdict(uid) ?? "allowed" });
      });
    return () => {
      alive = false;
    };
  }, [uid]);

  if (!uid) return "allowed";
  if (resolved && resolved.uid === uid) return resolved.value;
  // Sem resposta viva ainda: a semente durável responde na hora (BUG-198) e a
  // revalidação acima corrige ao vivo se o veredito mudou. Sem semente
  // (primeiro login neste aparelho, storage bloqueado), o fluxo é o de sempre:
  // "pending" segura a tela até o teto de 10s + fail-open.
  return readStoredVerdict(uid) ?? "pending";
}
